// Unified tracking API — fires events to all installed pixels (Meta, Google Ads, GA4, Reddit, Clarity).
// Usage: import { track } from '../lib/tracking'; track('ViewContent', { content_name: 'Pricing' });
//
// Supported standard events:
//   - ViewContent      (user views a key product/service page)
//   - InitiateCheckout (user clicks a Stripe Payment Link)
//   - Lead             (user submits contact/intake form)
//   - CompleteRegistration (user completes something trackable)
//   - AddToWishlist / AddToCart (intent signals)
//
// Custom events (for audience segmentation):
//   - ScrollDepth      ({ depth: 25|50|75|100 })
//   - TimeOnPage       ({ seconds: 30|60|120 })
//   - HighIntent       (fires after 60s+ on pricing/services pages)
//   - ExitIntent       (user moves cursor toward address bar)

const isGtag = () => typeof window !== "undefined" && typeof window.gtag === "function";
const isFbq = () => typeof window !== "undefined" && typeof window.fbq === "function";
const isRdt = () => typeof window !== "undefined" && typeof window.rdt === "function";
const isClarity = () => typeof window !== "undefined" && typeof window.clarity === "function";

// Map standard events to each platform's event name
const META_STANDARD = new Set([
  "ViewContent",
  "InitiateCheckout",
  "Lead",
  "CompleteRegistration",
  "AddToWishlist",
  "AddToCart",
  "Search",
  "Contact",
  "Purchase",
]);

const REDDIT_STANDARD_MAP = {
  ViewContent: "ViewContent",
  InitiateCheckout: "AddToCart",
  Lead: "Lead",
  CompleteRegistration: "SignUp",
  Purchase: "Purchase",
};

export function track(eventName, params = {}) {
  if (typeof window === "undefined") return;

  try {
    // Meta Pixel
    if (isFbq()) {
      if (META_STANDARD.has(eventName)) {
        window.fbq("track", eventName, params);
      } else {
        window.fbq("trackCustom", eventName, params);
      }
    }

    // GA4 (also serves Google Ads remarketing via linked property)
    if (isGtag()) {
      window.gtag("event", eventName, params);
    }

    // Reddit Pixel
    if (isRdt()) {
      const mapped = REDDIT_STANDARD_MAP[eventName];
      if (mapped) window.rdt("track", mapped, params);
      else window.rdt("track", "Custom", { customEventName: eventName, ...params });
    }

    // Microsoft Clarity — tag this session with the event for recording filters
    if (isClarity()) {
      window.clarity("set", "last_event", eventName);
    }
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[tracking] failed to dispatch", eventName, err);
    }
  }
}

// Convenience helper — fires ViewContent with structured data
export function trackPageView(contentName, extra = {}) {
  track("ViewContent", {
    content_name: contentName,
    content_category: extra.category || "page",
    currency: "USD",
    ...extra,
  });
}

// Convenience helper — fires InitiateCheckout (critical for cart-abandoner retargeting)
export function trackInitiateCheckout({ tier, priceUsd }) {
  track("InitiateCheckout", {
    content_name: tier,
    content_type: "product",
    value: priceUsd,
    currency: "USD",
    num_items: 1,
  });
}
